import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
    Users,
    Cpu,
    TrendingUp,
    Layers,
    Zap,
    Sparkles,
    Check,
} from "lucide-react";
import { SectionHeader } from "./Services";

const POINTS = [
    {
        icon: Users,
        title: "Human-verified data",
        desc: "Every record passes through trained interviewers and a second-line audit before delivery.",
        stat: { label: "Validation pass rate", value: "99.2%" },
        secret: "Each completed interview is sample-audited within 24h.",
    },
    {
        icon: Cpu,
        title: "Advanced CATI systems",
        desc: "Modern dialer, smart skip-logic, and live supervisor consoles ensure precision at scale.",
        stat: { label: "Supervisor uptime", value: "99.99%" },
        secret: "Live whisper-coach mode keeps interviewers consistent.",
    },
    {
        icon: TrendingUp,
        title: "High response rates",
        desc: "Profiled outreach and conversational design lift completion rates well above industry average.",
        stat: { label: "Avg. response rate", value: "47%" },
        secret: "Smart dial-windows match each market's call-friendly hours.",
    },
    {
        icon: Layers,
        title: "Scalable infrastructure",
        desc: "From 200 to 200,000 interviews — capacity flexes to your timeline without quality drift.",
        stat: { label: "Annual capacity", value: "200k+" },
        secret: "Burst capacity provisions in 48 hours, not 4 weeks.",
    },
    {
        icon: Zap,
        title: "Fast turnaround",
        desc: "Daily dashboards and weekly executive readouts keep teams aligned in real time.",
        stat: { label: "Time to insight", value: "7 days" },
        secret: "Topline counts visible from interview #1 onward.",
    },
];

/* random burst seeds */
const BURST_DOTS = Array.from({ length: 14 }).map((_, i) => {
    const angle = (i / 14) * Math.PI * 2;
    return {
        x: Math.cos(angle) * (60 + (i % 5) * 8),
        y: Math.sin(angle) * (60 + (i % 5) * 8),
        green: i % 3 === 0,
        delay: (i % 5) * 0.02,
    };
});

function WhyCard({ point, index }) {
    const Icon = point.icon;
    const [popped, setPopped] = useState(false);
    const [revealed, setRevealed] = useState(false);

    const trigger = () => {
        setPopped(true);
        setRevealed((v) => !v);
        setTimeout(() => setPopped(false), 800);
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.55, delay: index * 0.1 }}
            className="relative"
        >
            <motion.button
                type="button"
                onClick={trigger}
                whileHover={{ y: -8 }}
                whileTap={{ scale: 0.97 }}
                transition={{ type: "spring", stiffness: 280, damping: 18 }}
                className={`pr-card group rounded-2xl p-6 text-center relative w-full overflow-hidden focus:outline-none focus:ring-2 focus:ring-blue-400/40 ${
                    revealed
                        ? "border-emerald-400/40 shadow-[0_0_30px_rgba(34,197,94,0.25)]"
                        : ""
                }`}
                data-testid={`why-us-card-${index}`}
                aria-pressed={revealed}
                aria-label={`Reveal more about ${point.title}`}
            >
                {/* Glowing aura on hover */}
                <span className="absolute -top-12 -left-12 w-44 h-44 bg-blue-500/30 rounded-full blur-3xl opacity-0 group-hover:opacity-60 transition-opacity duration-500 pointer-events-none" />
                <span className="absolute -bottom-16 -right-12 w-40 h-40 bg-emerald-500/25 rounded-full blur-3xl opacity-0 group-hover:opacity-50 transition-opacity duration-500 pointer-events-none" />

                {/* Floating sparkles on hover */}
                <span className="pointer-events-none absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                    {[
                        { x: "18%", y: "22%", d: 0 },
                        { x: "78%", y: "30%", d: 0.4 },
                        { x: "26%", y: "78%", d: 0.8 },
                        { x: "82%", y: "70%", d: 1.2 },
                    ].map((s, i) => (
                        <motion.span
                            key={i}
                            className="absolute w-1 h-1 rounded-full bg-blue-300"
                            style={{ left: s.x, top: s.y }}
                            animate={{
                                y: [0, -8, 0],
                                opacity: [0.2, 1, 0.2],
                                scale: [0.8, 1.4, 0.8],
                            }}
                            transition={{
                                duration: 2.4,
                                repeat: Infinity,
                                delay: s.d,
                            }}
                        />
                    ))}
                </span>

                {/* Icon with morph */}
                <div className="relative z-10 w-14 h-14 mx-auto rounded-2xl bg-gradient-to-br from-blue-500/20 to-emerald-500/10 border border-blue-400/30 flex items-center justify-center transition-colors duration-500 group-hover:border-emerald-400/50 group-hover:from-emerald-500/15">
                    <AnimatePresence mode="wait" initial={false}>
                        {revealed ? (
                            <motion.span
                                key="check"
                                initial={{ rotate: -90, scale: 0.6, opacity: 0 }}
                                animate={{ rotate: 0, scale: 1, opacity: 1 }}
                                exit={{ rotate: 90, scale: 0.6, opacity: 0 }}
                                transition={{ duration: 0.35 }}
                            >
                                <Check className="w-6 h-6 text-emerald-300" />
                            </motion.span>
                        ) : (
                            <motion.span
                                key="icon"
                                initial={{ rotate: 90, scale: 0.6, opacity: 0 }}
                                animate={{ rotate: 0, scale: 1, opacity: 1 }}
                                exit={{ rotate: -90, scale: 0.6, opacity: 0 }}
                                transition={{ duration: 0.35 }}
                            >
                                <Icon className="w-6 h-6 text-blue-300 group-hover:text-emerald-300 transition-colors duration-500" />
                            </motion.span>
                        )}
                    </AnimatePresence>
                    <span className="pulse-ring absolute inset-0 rounded-2xl pointer-events-none" />
                </div>

                {/* Burst particles on click */}
                <AnimatePresence>
                    {popped &&
                        BURST_DOTS.map((d, i) => (
                            <motion.span
                                key={i}
                                initial={{ x: 0, y: 0, opacity: 1, scale: 1 }}
                                animate={{
                                    x: d.x,
                                    y: d.y,
                                    opacity: 0,
                                    scale: 0.4,
                                }}
                                exit={{ opacity: 0 }}
                                transition={{ duration: 0.7, delay: d.delay }}
                                className={`pointer-events-none absolute top-[50px] left-1/2 w-1.5 h-1.5 rounded-full ${
                                    d.green
                                        ? "bg-emerald-400 shadow-[0_0_10px_rgba(34,197,94,0.8)]"
                                        : "bg-blue-400 shadow-[0_0_10px_rgba(59,130,246,0.8)]"
                                }`}
                            />
                        ))}
                </AnimatePresence>

                {/* Title + desc */}
                <h3 className="relative z-10 mt-5 font-outfit text-base font-semibold text-white">
                    {point.title}
                </h3>
                <p className="relative z-10 mt-2 text-xs text-slate-400 leading-relaxed">
                    {point.desc}
                </p>

                {/* Hidden stat slides up on hover */}
                <div className="relative z-10 mt-4 h-7 overflow-hidden">
                    <motion.div
                        initial={{ y: 28, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        className="hidden group-hover:flex items-center justify-center gap-2"
                    >
                        <Sparkles className="w-3 h-3 text-emerald-300" />
                        <span className="font-mono-pr text-[10px] uppercase tracking-widest text-emerald-300">
                            {point.stat.label}
                        </span>
                        <span className="font-outfit text-xs font-bold text-white">
                            {point.stat.value}
                        </span>
                    </motion.div>
                    {/* default hint when not hovering */}
                    <p className="group-hover:hidden font-mono-pr text-[10px] uppercase tracking-[0.22em] text-blue-400/70 text-center pt-1">
                        Tap to reveal
                    </p>
                </div>

                {/* Click reveal panel */}
                <AnimatePresence>
                    {revealed && (
                        <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{ duration: 0.35 }}
                            className="relative z-10 mt-3 overflow-hidden"
                        >
                            <div className="rounded-xl border border-emerald-400/30 bg-emerald-500/[0.06] px-3 py-2.5 text-[11px] text-emerald-100/90 leading-relaxed">
                                <span className="font-mono-pr text-emerald-300 mr-1">
                                    secret · 0{index + 1}
                                </span>
                                {point.secret}
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.button>

            {/* Surprise badge on click */}
            <AnimatePresence>
                {revealed && (
                    <motion.span
                        initial={{ scale: 0, rotate: -25 }}
                        animate={{ scale: 1, rotate: -10 }}
                        exit={{ scale: 0, opacity: 0 }}
                        transition={{ type: "spring", bounce: 0.6 }}
                        className="absolute -top-2 -right-2 px-2 py-1 rounded-full bg-emerald-500 text-[9px] font-mono-pr font-bold text-emerald-950 tracking-wider shadow-[0_0_18px_rgba(34,197,94,0.7)]"
                    >
                        REVEALED
                    </motion.span>
                )}
            </AnimatePresence>
        </motion.div>
    );
}

export default function WhyUs() {
    return (
        <section
            className="relative py-28 overflow-hidden"
            data-testid="why-us-section"
        >
            <div className="absolute inset-0 bg-grid bg-grid-fade opacity-30 pointer-events-none" />
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] bg-blue-700/10 rounded-full blur-[140px] pointer-events-none" />

            <div className="pr-container relative">
                <SectionHeader
                    overline="Why Proksy"
                    title="Built for precision, designed for scale"
                    subtitle="Every layer of our stack — human and digital — is tuned for one outcome: data you can defend in a boardroom. Hover for a stat, tap to reveal a secret."
                />

                <div className="mt-16 relative">
                    <svg
                        className="absolute inset-x-0 top-1/2 -translate-y-1/2 h-px w-full hidden lg:block"
                        preserveAspectRatio="none"
                        viewBox="0 0 1200 4"
                    >
                        <defs>
                            <linearGradient id="connector" x1="0" x2="1">
                                <stop
                                    offset="0%"
                                    stopColor="#3B82F6"
                                    stopOpacity="0"
                                />
                                <stop offset="50%" stopColor="#22C55E" />
                                <stop
                                    offset="100%"
                                    stopColor="#3B82F6"
                                    stopOpacity="0"
                                />
                            </linearGradient>
                        </defs>
                        <motion.line
                            x1="0"
                            x2="1200"
                            y1="2"
                            y2="2"
                            stroke="url(#connector)"
                            strokeWidth="2"
                            initial={{ pathLength: 0 }}
                            whileInView={{ pathLength: 1 }}
                            viewport={{ once: true }}
                            transition={{ duration: 2.4, ease: "easeInOut" }}
                        />
                    </svg>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
                        {POINTS.map((p, i) => (
                            <WhyCard key={p.title} point={p} index={i} />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
